/**
 * CategoryHome — the flat, square category-tile landing page for
 * /labeling-overview. One tile per `LabelingCategoryKey` (seg / reporter /
 * triage / other); clicking a tile drills into `CategoryView` at
 * `/labeling-overview/:category`, which branches by role.
 *
 * Markup/idiom mirrors the approved mockup's `#s-home` `.cat` tiles
 * (PLANS/reviews/2026-07-07-labeling-overview-redesign-mockup.html):
 * mono key, name, description, a "work waiting" `StatusBadge` + left
 * accent edge when the role's active-work source has entries in that
 * category, and a trailing project count + arrow.
 */

import { useMemo } from "react";
import { useNavigate } from "react-router-dom";

import { useFactoryProjectsQuery, useMyEnrollmentsQuery } from "../../../hooks/useFactoryQueries";
import { useProjectsQuery } from "../../../hooks/useLabelingQueries";
import { useLabelingRole, type LabelingRole } from "../../../hooks/useLabelingRole";
import {
  bucketProjects,
  CATEGORIES,
  type LabelingCategoryKey,
} from "../../../utils/labelingCategories";
import { categoryAccent } from "./categoryAccent";
import { CountStrip, StatusBadge } from "./primitives";

function countLabel(count: number, singular: string): string {
  if (singular === "category") return `${count} ${count === 1 ? "category" : "categories"}`;
  return `${count} ${count === 1 ? singular : `${singular}s`}`;
}

const WORKLIST_BACKUP_ID_REGEX = /\.\d{8}T\d{6}Z$/;

/**
 * Categories the current role has active work in — drives the "work
 * waiting" badge + left accent edge on the home tiles.
 *
 * Only annotators get a real per-category signal today. `/my-enrollments` is
 * factory (segmentation) only — it does NOT carry `project_type` (B1) — so an
 * active/pending-approval enrollment always belongs to the 3d-segmentation
 * tile. Admin/internal have no equivalent source yet — the factory project list
 * (`useFactoryProjectsQuery`) has no `project_type` field to bucket by, and
 * guessing one (e.g. defaulting every factory project into "triage-validation")
 * would be a silently wrong signal. Left empty for those roles until the admin
 * control room (Tasks 7/10) has real per-project pending-approval data.
 */
function useActiveWorkKeys(role: LabelingRole): ReadonlySet<LabelingCategoryKey> {
  const myEnrollmentsQuery = useMyEnrollmentsQuery();

  return useMemo(() => {
    const keys = new Set<LabelingCategoryKey>();
    if (role !== "annotator") return keys;
    for (const enrollment of myEnrollmentsQuery.data ?? []) {
      if (
        !enrollment.annotation_read_only &&
        (enrollment.state === "active" || enrollment.state === "pending_approval")
      ) {
        // Factory enrollments are segmentation-only; do not derive the bucket
        // from project_type (the backend omits it — see B1).
        keys.add("3d-segmentation");
      }
    }
    return keys;
  }, [role, myEnrollmentsQuery.data]);
}

export function CategoryHome() {
  const navigate = useNavigate();
  const role = useLabelingRole();
  const projectsQuery = useProjectsQuery();
  const factoryProjectsQuery = useFactoryProjectsQuery();
  const activeWorkKeys = useActiveWorkKeys(role);

  const projects = useMemo(
    () =>
      (projectsQuery.data?.projects ?? []).filter(
        (project) => !WORKLIST_BACKUP_ID_REGEX.test(project.project_id),
      ),
    [projectsQuery.data],
  );
  const factoryProjects = useMemo(
    () => factoryProjectsQuery.data ?? [],
    [factoryProjectsQuery.data],
  );
  const buckets = useMemo(() => bucketProjects(projects), [projects]);
  const categoriesWithWork = CATEGORIES.filter((c) => activeWorkKeys.has(c.key)).length;
  const showFactoryProjectCounts = role === "admin" || role === "manager" || role === "internal";
  const projectCount = showFactoryProjectCounts ? factoryProjects.length : projects.length;

  return (
    <div className="h-full overflow-auto text-white page-premium">
      <div className="max-w-4xl mx-auto px-6 py-8">
        <div className="mb-6">
          <h1 className="text-2xl font-bold text-fg">Labeling</h1>
          <div className="mt-3">
            <CountStrip
              items={[
                countLabel(CATEGORIES.length, "category"),
                countLabel(projectCount, "project"),
                countLabel(projects.length, "worklist"),
                ...(categoriesWithWork > 0 ? [`work waiting in ${categoriesWithWork}`] : []),
              ]}
            />
          </div>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 gap-3.5">
          {CATEGORIES.map((category) => {
            const hasWork = activeWorkKeys.has(category.key);
            const count = buckets[category.key].length;
            const accent = categoryAccent(category.key);
            return (
              <button
                key={category.key}
                type="button"
                onClick={() => navigate(`/labeling-overview/${category.key}`)}
                className="group relative flex min-h-[184px] flex-col rounded border border-border/50 bg-surface p-5 text-left transition hover:border-border-strong"
              >
                <span
                  aria-hidden="true"
                  className={`pointer-events-none absolute inset-0 rounded opacity-0 transition-opacity duration-150 group-hover:opacity-100 ${accent.tint}`}
                />
                <span className={`absolute bottom-4 left-0 top-4 w-0.5 ${accent.edge}`} />
                <span
                  className={`font-mono text-[10px] uppercase tracking-[0.18em] ${accent.text}`}
                >
                  {category.mono}
                </span>
                <span className="mt-3.5 text-[19px] font-semibold tracking-tight text-fg">
                  {category.name}
                </span>
                <span className="mt-1.5 max-w-[34ch] text-[12.5px] text-fg-muted">
                  {category.description}
                </span>
                <span className="flex-1" />
                <div className="mt-4 flex items-center gap-2.5">
                  {hasWork && <StatusBadge tone="sig">Work waiting</StatusBadge>}
                  <span className="font-mono text-xs text-fg-muted">
                    {showFactoryProjectCounts && category.key === "3d-segmentation"
                      ? `${countLabel(factoryProjects.length, "project")} · ${countLabel(count, "worklist")}`
                      : countLabel(count, "worklist")}
                  </span>
                  <span className="ml-auto text-fg-faint">→</span>
                </div>
              </button>
            );
          })}
        </div>
      </div>
    </div>
  );
}

export default CategoryHome;
